import LocationDetails from "@bmi-digital/components/location-details";
import { graphql } from "gatsby";
import React from "react";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { GetMicroCopy } from "./MicroCopy";
import RichText, { RichTextData } from "./RichText";
import { useSiteContext } from "./Site";
import type { LocationDetailProps } from "@bmi-digital/components/location-details";
import type { PayloadContactDetails } from "../types/payload";

export type ContactDetailsData = Pick<
  PayloadContactDetails,
  "blockType" | "title" | "address" | "phoneNumber" | "email"
> & {
  otherInformation: RichTextData | null;
};

type Details = readonly [LocationDetailProps, ...LocationDetailProps[]];

const createDetail = ({
  type,
  text,
  actionStr,
  label,
  gtmLabel,
  external
}: {
  type: LocationDetailProps["type"];
  text: string;
  actionStr: string;
  label: string;
  gtmLabel?: string;
  external?: boolean;
}): LocationDetailProps => {
  const action: LocationDetailProps["action"] = {
    href: actionStr,
    gtm: {
      id: "cta-click1",
      action: actionStr,
      label: gtmLabel ? `${gtmLabel} - ${text}` : text
    }
  };

  if (external) {
    action.external = external;
  }

  return { type, text, action, label };
};

export type GetDetailsOptions = {
  getMicroCopy: GetMicroCopy;
  address: string | null;
  phoneNumber: string | null;
  email: string | null;
  gtmLabel?: string;
};

type DetailConfig = {
  type: LocationDetailProps["type"];
  val: string | null;
  prefix: string;
  copyValue: Parameters<GetMicroCopy>[0];
  ext?: boolean;
};

const buildDetails = (
  configs: DetailConfig[],
  getMicroCopy: GetMicroCopy,
  gtmLabel?: string
): LocationDetailProps[] =>
  configs.reduce<LocationDetailProps[]>((acc, config) => {
    if (config.val) {
      acc.push(
        createDetail({
          type: config.type,
          text: config.val,
          actionStr: `${config.prefix}${config.val}`,
          label: getMicroCopy(config.copyValue),
          gtmLabel,
          external: config.ext
        })
      );
    }
    return acc;
  }, []);

export const getDetails = ({
  getMicroCopy,
  address,
  phoneNumber,
  email,
  gtmLabel
}: GetDetailsOptions): Details => {
  const configs: DetailConfig[] = [
    {
      type: "address",
      val: address,
      prefix: "https://maps.google.com/maps?q=",
      copyValue: microCopy.GLOBAL_ADDRESS,
      ext: true
    },
    {
      type: "phone",
      val: phoneNumber,
      prefix: "tel:",
      copyValue: microCopy.GLOBAL_TELEPHONE
    },
    {
      type: "email",
      val: email,
      prefix: "mailto:",
      copyValue: microCopy.GLOBAL_EMAIL
    }
  ];

  const [firstDetail, ...restDetails] = buildDetails(
    configs,
    getMicroCopy,
    gtmLabel
  );

  return [firstDetail, ...restDetails];
};

const IntegratedLocationCard = ({
  data,
  gtmLabel
}: {
  data: ContactDetailsData;
  gtmLabel?: string;
}): React.ReactElement => {
  const { title, address, phoneNumber, email, otherInformation } = data;
  const { getMicroCopy } = useSiteContext();
  const details = getDetails({
    getMicroCopy,
    address,
    phoneNumber,
    email,
    gtmLabel
  });

  return (
    <LocationDetails
      title={title}
      details={details}
      footNote={
        otherInformation ? (
          <RichText document={otherInformation} hasNoBottomMargin />
        ) : undefined
      }
    />
  );
};

export default IntegratedLocationCard;

export const query = graphql`
  fragment ContactDetailsFragment on PayloadContactDetails {
    blockType
    title
    address
    phoneNumber
    email
    otherInformation {
      ...RichTextFragment
    }
  }
`;
